// src/api/client.js
// Thin fetch wrapper around the Express backend.
// - same-origin (cookies sent) so the httpOnly JWT cookie flows automatically
// - AbortSignal.timeout on every call (BP-JS-2: never let an external call hang)
// - normalised errors with status + parsed body

const DEFAULT_TIMEOUT = 30_000;

export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function request(method, path, { body, timeout = DEFAULT_TIMEOUT } = {}) {
  let res;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'include',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      signal: AbortSignal.timeout(timeout),
    });
  } catch (err) {
    if (err.name === 'TimeoutError') throw new ApiError('Request timed out', { status: 0 });
    throw new ApiError('Network error', { status: 0 });
  }

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const msg = (isJson && payload?.error) || `Request failed (${res.status})`;
    throw new ApiError(msg, { status: res.status, body: payload });
  }
  return payload;
}

export const api = {
  get:  (path, opts)       => request('GET', path, opts),
  post: (path, body, opts) => request('POST', path, { ...opts, body }),
  put:  (path, body, opts) => request('PUT', path, { ...opts, body }),
  del:  (path, opts)       => request('DELETE', path, opts),
};
